import { View, Text, TouchableOpacity, Animated } from "react-native";
import React, { useState, useEffect, useRef } from "react";
import Colors from "../../Resources/Color";
import { deleteField } from "firebase/firestore";
import { Ionicons } from "@expo/vector-icons";
import { useScreenDimensions, useDevice } from "../_layout";
import { LinearGradient } from "expo-linear-gradient";
import { useTranslation } from "react-i18next";

const ProtocolInfoSection = ({ coach, animated = false }) => {
  const { t, i18n } = useTranslation();
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();
  const [isCloseHovered, setIsCloseHovered] = useState(false);
  const heightAnim = useRef(new Animated.Value(animated ? 0 : 1)).current;
  const opacityAnim = useRef(new Animated.Value(animated ? 0 : 1)).current;

  useEffect(() => {
    if (animated) {
      Animated.parallel([
        Animated.timing(heightAnim, {
          toValue: 1,
          duration: 400,
          useNativeDriver: false,
        }),
        Animated.timing(opacityAnim, {
          toValue: 1,
          duration: 300,
          delay: 100,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [animated]);

  const ExampleTag = ({ text }) => (
    <View
      style={{
        backgroundColor: Colors.whiteColor,
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 10,
        alignSelf: "flex-start",
        // borderWidth: 1,
        // borderColor: "rgba(0,0,0,0.08)",
      }}
    >
      <Text
        style={{
          fontSize: 12,
          color: Colors.blackColor,
          fontWeight: "500",
        }}
      >
        {text}
      </Text>
    </View>
  );

  const onPress = () => {
    coach.setProperty("showingProtocolsGuide", false);
    coach.updateCoachPublicProfile({
      showing_protocols_guide: deleteField(),
    });
  };

  return (
    <Animated.View
      style={{
        width: "100%",
        maxHeight: animated
          ? heightAnim.interpolate({
              inputRange: [0, 1],
              outputRange: [0, 1000],
            })
          : undefined,
        opacity: opacityAnim,
        overflow: "hidden",
      }}
    >
      <LinearGradient
        colors={["rgba(244, 244, 244, 1)", "rgba(238, 238, 238, 1)"]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={{
          borderRadius: 14,
          borderWidth: 1,
          borderColor: "rgba(0,0,0,0.05)",
          padding: 16,
          width: "100%",
          position: "relative",
        }}
      >
        <TouchableOpacity
          style={{
            position: "absolute",
            top: 16,
            right: 16,
            zIndex: 10,
            padding: 2,
            opacity: isCloseHovered ? 0.5 : 1,
            backgroundColor: "rgba(0,0,0,0.05)",
            borderRadius: 12,
          }}
          onPress={onPress}
          onMouseEnter={() => !isMobile && setIsCloseHovered(true)}
          onMouseLeave={() => setIsCloseHovered(false)}
        >
          <Ionicons name="close" size={16} color={Colors.darkgrayColor} />
        </TouchableOpacity>

        <View
          style={{
            flexDirection: screenSize === "small" ? "column" : "row",
            gap: screenSize === "small" ? 16 : 24,
            alignItems: "flex-start",
          }}
        >
          <View
            style={{
              minWidth: screenSize === "small" ? "100%" : 0,
              ...(screenSize !== "small" && {
                flex: 1,
                flexShrink: 10,
              }),
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 12,
                marginBottom: 12,
              }}
            >
              <View
                style={{
                  backgroundColor: Colors.whiteColor,
                  width: 34,
                  height: 34,
                  borderRadius: 17,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Ionicons
                  name="information-circle"
                  size={20}
                  color={Colors.blackColor}
                />
              </View>
              <Text
                style={{
                  fontSize: 15,
                  fontWeight: "600",
                  color: Colors.blackColor,
                }}
              >
                {t("protocolsGuide-string")}
              </Text>
            </View>

            <Text
              style={{
                fontSize: 13,
                color: Colors.darkgrayColor,
                lineHeight: 20,
              }}
            >
              {t("protocolsGuideDescription-string")}
            </Text>
          </View>

          <View
            style={{
              flexDirection: screenSize === "small" ? "column" : "row",
              gap: screenSize === "small" ? 16 : 16,
              alignItems: "flex-start",
              minWidth: screenSize === "small" ? "100%" : 0,
              justifyContent: "center",
              ...(screenSize !== "small" && {
                flex: 2,
                flexShrink: 1,
              }),
            }}
          >
            <View
              style={{
                width: screenSize === "small" ? "100%" : "auto",
                maxWidth: screenSize === "small" ? "100%" : 280,
                ...(screenSize !== "small" && {
                  flex: 1,
                }),
              }}
            >
              <Text
                style={{
                  fontSize: 11,
                  fontWeight: "600",
                  color: Colors.darkgrayColor,
                  marginBottom: 12,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                {t("protocolsFixedRepsRPE-string")}
              </Text>
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "flex-start",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <View style={{ gap: 8 }}>
                  <ExampleTag text="Single @5" />
                  <ExampleTag text="Ramp 3x4 @7" />
                </View>
                <View style={{ gap: 8 }}>
                  <ExampleTag text="x4 @5" />
                  <ExampleTag text="3x4 @5" />
                </View>
                <View style={{ gap: 8 }}>
                  <ExampleTag text="x4 @4-5-6" />
                </View>
              </View>
            </View>

            <View
              style={{
                width: screenSize === "small" ? "100%" : "auto",
                maxWidth: screenSize === "small" ? "100%" : 180,
                ...(screenSize !== "small" && {
                  flex: 0.5,
                }),
              }}
            >
              <Text
                style={{
                  fontSize: 11,
                  fontWeight: "600",
                  color: Colors.darkgrayColor,
                  marginBottom: 12,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                {t("protocolsFixedWeightReps-string")}
              </Text>
              <View style={{ gap: 8 }}>
                <ExampleTag text="175-180-185 x4" />
                <ExampleTag text="3x5x200" />
              </View>
            </View>
          </View>

          <View
            style={{
              alignSelf: screenSize === "small" ? "flex-start" : "center",
              // minWidth: screenSize === "small" ? "100%" : 0,
              ...(screenSize !== "small" && {
                flex: 1,
                flexShrink: 10,
              }),
            }}
          >
            <View
              style={{
                padding: 8,
                backgroundColor: Colors.whiteColor,
                borderRadius: 10,
              }}
            >
              <Text
                style={{
                  fontSize: 13,
                  color: Colors.blackColor,
                  lineHeight: 18,
                  fontWeight: "500",
                }}
              >
                {t("protocolsCombineWith-string")}{" "}
                <Text style={{ fontWeight: "bold", color: Colors.blackColor }}>
                  {t("protocolsCombineSymbol-string")}
                </Text>
                {i18n.language === "es" ? "." : " symbol."}
              </Text>
              <Text
                style={{
                  fontSize: 12,
                  color: Colors.darkgrayColor,
                  marginTop: 4,
                  fontStyle: "italic",
                }}
              >
                {t("protocolsCombineExample-string")}
              </Text>
            </View>
          </View>
        </View>
      </LinearGradient>
    </Animated.View>
  );
};

export default ProtocolInfoSection;
